/**
 * Helper bersama untuk monitoring antrean pemrosesan Knowledge Base.
 *
 * Dipakai oleh halaman /ai/historykb dan modal Knowledge Base di /agent-ai-v2
 * agar label status, normalisasi response, dan mekanisme polling tidak
 * diimplementasikan dua kali.
 *
 * Kontrak API mengikuti docs/knowledge-base-processing-queue-api.md:
 * - GET /omni/knowledge-base/jobs/{job_id}
 * - GET /omni/knowledge-base/jobs/{job_id}/chunks
 * - GET /omni/knowledge-base/jobs/latest?file_id=...
 */

export const KB_POLL_INTERVAL_MS = 3000;

export const KB_JOB_ACTIVE_STATUSES = ["queued", "processing", "finalizing"];
export const KB_JOB_TERMINAL_STATUSES = ["success", "partial_success", "failed", "cancelled"];

const JOB_STATUS_META = {
  queued: { label: "Menunggu", tone: "muted" },
  processing: { label: "Sedang diproses", tone: "processing" },
  finalizing: { label: "Finalisasi", tone: "processing" },
  success: { label: "Tersimpan", tone: "success" },
  partial_success: { label: "Sebagian berhasil", tone: "warning" },
  failed: { label: "Gagal", tone: "danger" },
  cancelled: { label: "Dibatalkan", tone: "neutral" }
};

const CHUNK_STATUS_META = {
  queued: { label: "Menunggu", tone: "muted" },
  processing: { label: "Sedang diproses", tone: "processing" },
  embedded: { label: "Embedding selesai", tone: "processing" },
  success: { label: "Tersimpan", tone: "success" },
  failed: { label: "Gagal", tone: "danger" }
};

const JOB_STAGE_META = {
  extracting: "Ekstraksi teks",
  splitting: "Pembagian bagian",
  embedding: "Embedding",
  storing_qdrant: "Penyimpanan Qdrant",
  finalizing: "Finalisasi",
  done: "Selesai"
};

function toNumber(value) {
  const number = Number(value);
  if (!isFinite(number) || number < 0) return 0;
  return number;
}

function normalizeStatus(value) {
  return String(value || "").trim().toLowerCase();
}

export function isKbJobActive(status) {
  return KB_JOB_ACTIVE_STATUSES.indexOf(normalizeStatus(status)) !== -1;
}

export function isKbJobTerminal(status) {
  return KB_JOB_TERMINAL_STATUSES.indexOf(normalizeStatus(status)) !== -1;
}

export function kbJobStatusMeta(status) {
  const key = normalizeStatus(status);
  if (JOB_STATUS_META[key]) return JOB_STATUS_META[key];
  return { label: key ? key : "Tidak diketahui", tone: "muted" };
}

export function kbChunkStatusMeta(status) {
  const key = normalizeStatus(status);
  if (CHUNK_STATUS_META[key]) return CHUNK_STATUS_META[key];
  return { label: key ? key : "Tidak diketahui", tone: "muted" };
}

export function kbJobStageLabel(stage) {
  const key = normalizeStatus(stage);
  return JOB_STAGE_META[key] || (key ? key : "");
}

/**
 * Normalisasi response detail job agar field progress selalu bertipe angka.
 * Nilai persentase tetap memakai field `progress.percent` dari backend.
 * Backend sudah membatasi <100 untuk job non-success; clamp di sini bersifat
 * defensif agar job lama tidak pernah tampil 100% sebelum benar-benar sukses.
 */
export function normalizeKbJob(raw) {
  if (!raw || !raw.job_id) return null;
  const progress = raw.progress || {};
  const status = normalizeStatus(raw.status);
  let percent = Math.max(0, Math.min(100, Number(progress.percent) || 0));
  if (status !== "success" && percent >= 100) percent = 99;
  const completed = toNumber(progress.completed);
  const processed = progress.processed === undefined ? completed : toNumber(progress.processed);
  const stage = raw.stage ? String(raw.stage) : "";
  const currentStage = raw.current_stage ? String(raw.current_stage) : stage;
  return {
    job_id: String(raw.job_id),
    file_id: toNumber(raw.file_id),
    history_id: toNumber(raw.history_id),
    member_id: toNumber(raw.member_id),
    license_id: raw.license_id ? String(raw.license_id) : "",
    filename: raw.filename ? String(raw.filename) : "",
    status: status,
    stage: stage,
    current_stage: currentStage,
    content_version: toNumber(raw.content_version),
    job_trigger: raw.job_trigger ? String(raw.job_trigger) : "",
    progress: {
      total: toNumber(progress.total),
      queued: toNumber(progress.queued),
      processing: toNumber(progress.processing),
      embedded: toNumber(progress.embedded),
      completed: completed,
      processed: processed,
      failed: toNumber(progress.failed),
      percent: percent
    },
    current_chunk: raw.current_chunk === null || raw.current_chunk === undefined ? null : toNumber(raw.current_chunk),
    batch_size: toNumber(raw.batch_size),
    message: raw.message ? String(raw.message) : "",
    created_at: raw.created_at || null,
    started_at: raw.started_at || null,
    updated_at: raw.updated_at || null,
    finished_at: raw.finished_at || null,
    last_error: raw.last_error ? String(raw.last_error) : ""
  };
}

export function formatKbPercent(value) {
  const number = Math.max(0, Math.min(100, Number(value) || 0));
  const rounded = Math.round(number * 100) / 100;
  const text = rounded % 1 === 0 ? String(rounded) : rounded.toFixed(2);
  return text.replace(".", ",") + "%";
}

/**
 * Pesan error job yang aman ditampilkan ke pengguna. Detail teknis dari
 * backend (nama service, host, stack trace) tidak pernah ditampilkan.
 */
export function safeKbJobError(job) {
  if (!job) return "";
  const raw = normalizeStatus(job.last_error);
  if (job.status === "cancelled") {
    return "Pemrosesan dokumen dibatalkan. Versi dokumen sebelumnya tetap digunakan.";
  }
  if (job.status === "partial_success") {
    return "Sebagian bagian dokumen belum berhasil disimpan.";
  }
  if (job.status !== "failed") return "";
  if (raw.indexOf("antrean") !== -1 || raw.indexOf("queue") !== -1) {
    return "Dokumen gagal dimasukkan ke antrean pemrosesan. Silakan unggah ulang dokumen.";
  }
  if (raw.indexOf("retry") !== -1 || raw.indexOf("habis") !== -1) {
    return "Pemrosesan dokumen gagal setelah beberapa kali percobaan. Versi dokumen sebelumnya tetap digunakan.";
  }
  return "Pemrosesan dokumen belum berhasil. Versi dokumen sebelumnya tetap digunakan.";
}

const INTERNAL_ERROR_PATTERN = /(ollama|mxbai|embedding model|redis|asynq|qdrant|localhost|127\.0\.0\.1|connection refused|econn|timeout|stack trace|goroutine|panic)/i;

/**
 * Pesan error per chunk. Error teknis diganti pesan umum agar tidak
 * membocorkan detail internal.
 */
export function safeKbChunkError(chunk) {
  if (!chunk || normalizeStatus(chunk.status) !== "failed") return "";
  const raw = String(chunk.error_message || "").replace(/https?:\/\/\S+/gi, " ").replace(/\s+/g, " ").trim();
  if (!raw || INTERNAL_ERROR_PATTERN.test(raw)) {
    return "Bagian ini gagal diproses setelah beberapa percobaan.";
  }
  return raw.length > 160 ? raw.slice(0, 157) + "..." : raw;
}

/**
 * Status HTTP dari error requestRaw/axios. Dipakai untuk membedakan
 * "dokumen lama tanpa job" (404) dari gangguan jaringan sementara.
 */
export function kbErrorHttpStatus(error) {
  if (!error) return 0;
  if (error.response && error.response.status) return Number(error.response.status) || 0;
  const message = String(error.message || error.msg || "");
  if (/tidak ditemukan|not found/i.test(message)) return 404;
  if (/tidak dapat diakses|forbidden/i.test(message)) return 403;
  if (/sesi login tidak valid|unauthorized/i.test(message)) return 401;
  return 0;
}

/**
 * Cegah job lama menimpa job yang lebih baru untuk file yang sama
 * (misalnya saat user mengunggah ulang dokumen).
 */
export function shouldApplyKbJob(previous, next) {
  if (!next) return false;
  if (!previous) return true;
  if (previous.job_id === next.job_id) return true;
  const previousTime = Date.parse(previous.created_at || "") || 0;
  const nextTime = Date.parse(next.created_at || "") || 0;
  return nextTime >= previousTime;
}

/**
 * Monitor polling per job_id.
 *
 * - Setiap job memiliki timer sendiri sehingga beberapa dokumen dapat
 *   diproses bersamaan tanpa status tertukar.
 * - Request berikutnya baru dijadwalkan setelah request sebelumnya selesai
 *   (tidak ada request yang tumpang tindih).
 * - Polling berhenti otomatis saat status terminal.
 * - Error jaringan tidak mengubah status job; polling dilanjutkan dengan
 *   backoff ringan. Error 401/403/404 menghentikan polling job tersebut.
 */
export function createKbJobMonitor(options) {
  const opts = options || {};
  const fetchJob = opts.fetchJob;
  const onUpdate = typeof opts.onUpdate === "function" ? opts.onUpdate : function () {};
  const onError = typeof opts.onError === "function" ? opts.onError : function () {};
  const intervalMs = toNumber(opts.intervalMs) || KB_POLL_INTERVAL_MS;
  const maxBackoffMs = 30000;

  const entries = {};
  let disposed = false;

  function clearEntry(jobId) {
    const entry = entries[jobId];
    if (!entry) return;
    if (entry.timer) clearTimeout(entry.timer);
    delete entries[jobId];
  }

  function schedule(jobId, delay) {
    const entry = entries[jobId];
    if (!entry || disposed) return;
    if (entry.timer) clearTimeout(entry.timer);
    entry.timer = setTimeout(function () {
      entry.timer = null;
      poll(jobId);
    }, Math.max(0, delay));
  }

  function poll(jobId) {
    const entry = entries[jobId];
    if (!entry || disposed || entry.inFlight) return;
    entry.inFlight = true;
    Promise.resolve()
      .then(function () {
        return fetchJob(jobId);
      })
      .then(function (response) {
        entry.inFlight = false;
        if (disposed || !entries[jobId]) return;
        const job = normalizeKbJob(response && response.data);
        if (!job) {
          clearEntry(jobId);
          return;
        }
        entry.failures = 0;
        onUpdate(job);
        if (isKbJobActive(job.status)) {
          schedule(jobId, intervalMs);
        } else {
          clearEntry(jobId);
        }
      })
      .catch(function (error) {
        entry.inFlight = false;
        if (disposed || !entries[jobId]) return;
        const status = kbErrorHttpStatus(error);
        onError(error, jobId);
        if (status === 401 || status === 403 || status === 404) {
          clearEntry(jobId);
          return;
        }
        entry.failures = (entry.failures || 0) + 1;
        const backoff = Math.min(intervalMs * entry.failures, maxBackoffMs);
        schedule(jobId, backoff);
      });
  }

  return {
    track: function (jobId) {
      const id = String(jobId || "");
      if (!id || disposed || entries[id]) return;
      entries[id] = { timer: null, inFlight: false, failures: 0 };
      schedule(id, intervalMs);
    },
    untrack: function (jobId) {
      const id = String(jobId || "");
      if (!id) return;
      clearEntry(id);
    },
    untrackAll: function () {
      Object.keys(entries).forEach(clearEntry);
    },
    isTracking: function (jobId) {
      return !!entries[String(jobId || "")];
    },
    dispose: function () {
      disposed = true;
      Object.keys(entries).forEach(clearEntry);
    }
  };
}



// WEBPACK FOOTER //
// ./src/utils/knowledgeBaseQueue.js